import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { ArrowLeftRight, Loader2, MapPin, OctagonAlert, RotateCcw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EmptyState } from '@/components/ui/empty-state';
import { ListSkeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/workspace-dialog';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import { cn } from '@/lib/utils';
import { picklistService } from '../../services';
import { usePicklistStore } from '../../store/picklistStore';
import type { BinInfoDto, PicklistItemGroupDto, SwapSerialDto } from '../../types';

interface SwapSerialDialogProps {
  /** The item group and its taken serial display bin (whose `id` is the slot). Null: closed. */
  target: { group: PicklistItemGroupDto; bin: BinInfoDto } | null;
  picklistId: number;
  onClose: () => void;
}

/** A status's pill (E2, the handheld's labels and tones); Available has none. */
function statusPill(s: SwapSerialDto): { text: string; tone: string } | null {
  switch (s.status) {
    case 'Available':
      return null;
    case 'Reserved':
      return { text: `RESERVED · ${s.reservedBy?.picklistNumber ?? ''}`.trim(), tone: 'bg-warning/10 text-warning' };
    case 'PickedElsewhere':
      return { text: `PICKED · ${s.takenBy?.picklistNumber ?? ''}`.trim(), tone: 'bg-destructive/10 text-destructive' };
    case 'OnThisPicklist':
      return { text: 'ON THIS PICKLIST', tone: 'bg-muted text-muted-foreground' };
    case 'PickedHere':
      return { text: 'PICKED HERE', tone: 'bg-muted text-muted-foreground' };
    default:
      return { text: String(s.status).toUpperCase(), tone: 'bg-muted text-muted-foreground' };
  }
}

/**
 * PT-6 scope 16 — "Swap serial": give an unpicked slot, whose serial another picklist took, the serial this picklist
 * should pick instead (prototype L1131–1164). It opens only from the bin card's `Swap serial` (PT-7 C2).
 *
 * Every on-hand serial of the item is listed except the slot's own, each with its status (O-5 = keep D10/D10b): only
 * Available rows can be chosen, and the web has no take-anyway. Names never appear (O-4 b): the picklist number and the
 * server's time label.
 */
export function SwapSerialDialog({ target, picklistId, onClose }: SwapSerialDialogProps) {
  return (
    <Dialog open={target !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        hideCloseButton
        overlayClassName="bg-black/35"
        className="flex max-h-[min(600px,100%)] w-[520px] max-w-[calc(100%-48px)] flex-col gap-0 overflow-hidden rounded-[14px] bg-card p-0 sm:rounded-[14px]"
      >
        {target && <SwapBody group={target.group} bin={target.bin} picklistId={picklistId} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function SwapBody({ group, bin, picklistId, onClose }: { group: PicklistItemGroupDto; bin: BinInfoDto; picklistId: number; onClose: () => void }) {
  const [rows, setRows] = useState<SwapSerialDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);

  // One NetSuite read, when the dialog opens (and on Reload serials); never per row.
  const load = useCallback(async () => {
    setRows(null);
    setLoadError(null);
    try {
      setRows(await picklistService.getReplacementSerials(picklistId, group.id, bin.id));
    } catch (error) {
      setLoadError(getApiErrorMessage(error, ''));
    }
  }, [picklistId, group.id, bin.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const needle = query.trim().toLowerCase();
  const shown = (rows ?? []).filter((r) => !needle || r.inventoryNumber.toLowerCase().includes(needle) || r.binCode.toLowerCase().includes(needle));
  const chosen = (rows ?? []).find((r) => r.inventoryNumberId === selected) ?? null;
  const old = bin.inventoryNumber ?? String(bin.inventoryNumberId ?? '');
  const taken = bin.takenBy;
  const n = rows?.length ?? 0;

  const use = async () => {
    if (!chosen || bin.inventoryNumberId == null) return;
    setBusy(true);
    try {
      await usePicklistStore.getState().replaceSerial(group.id, {
        picklistItemBinId: bin.id,
        expectedInventoryNumberId: bin.inventoryNumberId,
        newInventoryNumberId: chosen.inventoryNumberId,
        newInventoryNumber: chosen.inventoryNumber,
        newBinId: chosen.binId,
        newBinCode: chosen.binCode,
        newLocationId: chosen.locationId,
      });
      // No success toast: the card's taken state clearing is the confirmation (the prototype's).
      onClose();
    } catch (error) {
      const status = error instanceof AxiosError ? error.response?.status : undefined;
      toast.error(getApiErrorMessage(error, 'Couldn’t swap the serial. Nothing was changed.'));
      if (status === 409) {
        // The slot changed under us: close and show the new figures.
        onClose();
        void usePicklistStore.getState().reloadSelectedPicklist(picklistId);
      }
    } finally {
      setBusy(false);
    }
  };

  // Arrow keys move between rows; Space or Enter chooses an Available one (E5).
  const onListKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = [...(listRef.current?.querySelectorAll<HTMLElement>('[role="radio"]') ?? [])];
    const at = items.indexOf(document.activeElement as HTMLElement);
    const next = items[Math.max(0, Math.min(items.length - 1, at + (e.key === 'ArrowDown' ? 1 : -1)))];
    if (next) {
      e.preventDefault();
      next.focus();
    }
  };

  let list: ReactNode;
  if (rows === null && !loadError) {
    list = <ListSkeleton rows={4} className="px-[18px] py-3" />;
  } else if (loadError !== null) {
    list = (
      <EmptyState
        size="compact"
        title="Serials couldn't be loaded."
        description={loadError || undefined}
        action={
          <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={() => void load()}>
            <RotateCcw className="h-3 w-3" aria-hidden />
            Reload serials
          </Button>
        }
      />
    );
  } else if (n === 0) {
    list = <EmptyState size="compact" title="No other serials of this item are on hand." />;
  } else if (shown.length === 0) {
    list = <EmptyState size="compact" title="No serial matches that search." />;
  } else {
    list = shown.map((r, i) => {
      const available = r.status === 'Available';
      const on = selected === r.inventoryNumberId;
      const pill = statusPill(r);
      const firstFocusable = selected == null ? i === 0 : on;
      return (
        <div
          key={r.inventoryNumberId}
          role="radio"
          aria-checked={on}
          aria-disabled={!available}
          tabIndex={firstFocusable ? 0 : -1}
          onClick={() => { if (available) setSelected(r.inventoryNumberId); }}
          onKeyDown={(e) => {
            if ((e.key === ' ' || e.key === 'Enter') && available) {
              e.preventDefault();
              setSelected(r.inventoryNumberId);
            }
          }}
          className={cn(
            'box-border flex min-h-[42px] items-center gap-2.5 border-b border-border/60 px-[18px] py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
            available ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default',
            r.status === 'PickedHere' && 'opacity-55',
            on && 'bg-primary/[0.06]',
          )}
        >
          <span aria-hidden className={cn('flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-[1.5px]', on ? 'border-primary' : 'border-input')}>
            <span className={cn('h-2 w-2 rounded-full', on ? 'bg-primary' : 'bg-transparent')} />
          </span>
          <span className="font-mono text-[12.5px] font-semibold">{r.inventoryNumber}</span>
          <span className="inline-flex items-center gap-1 text-[11.5px] text-muted-foreground">
            <MapPin className="h-3 w-3" aria-hidden />
            {r.binCode}
          </span>
          <span className="ml-auto flex items-center gap-1.5">
            {pill && (
              <span className={cn('inline-flex h-5 items-center whitespace-nowrap rounded-full px-2 text-[10.5px] font-semibold', pill.tone)}>
                {pill.text}
              </span>
            )}
            {r.binId === bin.binId && (
              <span className="inline-flex h-5 items-center rounded-full bg-muted px-2 text-[11px] font-medium text-muted-foreground">Same bin</span>
            )}
          </span>
        </div>
      );
    });
  }

  return (
    <>
      <div className="flex items-start justify-between gap-3 px-[18px] pt-4">
        <div className="min-w-0">
          <DialogTitle className="text-[15px] font-bold">Swap serial</DialogTitle>
          <div className="mt-1 font-mono text-xs font-semibold text-muted-foreground">{group.itemCode}</div>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[7px] text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent>Close</TooltipContent>
        </Tooltip>
      </div>

      <DialogDescription asChild>
        <div className="mx-[18px] mt-2.5 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/[0.06] px-2.5 py-2 text-xs leading-normal text-destructive">
          <OctagonAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            <b className="font-mono">{old}</b> was picked on {taken?.picklistNumber ?? 'another picklist'}
            {taken?.pickedAtLabel ? ` ${taken.pickedAtLabel}` : ''}. Choose the serial this picklist should pick instead.
          </span>
        </div>
      </DialogDescription>

      <div className="flex items-center gap-2 border-b border-border px-[18px] py-2.5">
        <span className="text-xs font-semibold tabular-nums">
          {rows === null ? ' ' : `${n} ${n === 1 ? 'serial' : 'serials'} on hand`}
        </span>
      </div>

      {n > 6 && (
        <div className="border-b border-border px-[18px] py-2">
          {/* The rows are in hand: an in-memory filter, no request, no debounce. */}
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search serial or bin"
            aria-label="Search serial or bin"
            className="h-8 text-[12.5px] md:text-[12.5px]"
          />
        </div>
      )}

      <div
        ref={listRef}
        role="radiogroup"
        aria-label="Replacement serial"
        onKeyDown={onListKey}
        className="min-h-0 flex-1 overflow-y-auto"
      >
        {list}
      </div>

      <div className="flex justify-end gap-2 border-t border-border px-[18px] pb-3.5 pt-3">
        <Button variant="outline" className="h-9 text-[13px]" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button className="h-9 gap-1.5 text-[13px] font-semibold" disabled={!chosen || busy} onClick={() => void use()}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden /> : <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden />}
          {chosen ? `Use ${chosen.inventoryNumber}` : 'Choose a serial'}
        </Button>
      </div>
    </>
  );
}
